import React from 'react';
import { Button, Space, Spin, Card } from 'antd';
import { LeftOutlined, RightOutlined, CheckOutlined } from '@ant-design/icons';
import { useSkuMigration } from './hooks';
import { MIGRATION_STEPS } from './constants';
import StepSelectSkus from './steps/StepSelectSkus';
import StepMapNames from './steps/StepMapNames';
import StepReview from './steps/StepReview';

/**
 * outerStep is the outer ProductOnboarding currentStep.
 * Migrate SKUs occupies outer steps 1–3:
 *   1 → Select SKUs
 *   2 → Map Names
 *   3 → Review
 *
 * onOuterNext / onOuterPrev drive the outer Steps bar.
 * onBackToType resets outer step to 0 (type selection screen).
 */
export interface SkuMigrationProps {
  outerStep:     number;
  onOuterNext:   () => void;
  onOuterPrev:   () => void;
  onBackToType:  () => void;
}

// Map outer step (1-based) to the inner content index (0-based)
const OUTER_STEP_OFFSET = 1;

const SkuMigration: React.FC<SkuMigrationProps> = ({
  outerStep,
  onOuterNext,
  onOuterPrev,
  onBackToType,
}) => {
  const hook = useSkuMigration();

  // innerStep: 0 = Select, 1 = Map Names, 2 = Review
  const innerStep = outerStep - OUTER_STEP_OFFSET;
  const isFirstMigrateStep = innerStep === 0;
  const isLastMigrateStep  = innerStep === MIGRATION_STEPS.length - 1;

  const handleNext = () => {
    hook.goNext(onOuterNext);
  };

  const handlePrev = () => {
    if (isFirstMigrateStep) {
      onBackToType();
    } else {
      hook.goPrev(onOuterPrev);
    }
  };

  const renderContent = () => {
    switch (innerStep) {
      case 0:
        return (
          <StepSelectSkus
            loading={hook.loading}
            filters={hook.filters}
            setFilters={hook.setFilters}
            filteredSkus={hook.filteredSkus}
            selectedSkuIds={hook.selectedSkuIds}
            toggleSkuSelection={hook.toggleSkuSelection}
            toggleSelectAll={hook.toggleSelectAll}
            importCsv={hook.importCsv}
            csvImported={hook.csvImported}
          />
        );
      case 1:
        return (
          <StepMapNames
            conversionType={hook.filters.conversionType}
            migrationRows={hook.migrationRows}
            rowErrors={hook.rowErrors}
            updateTargetName={hook.updateTargetName}
          />
        );
      case 2:
        return (
          <StepReview
            conversionType={hook.filters.conversionType}
            configSummary={hook.configSummary}
            submitting={hook.submitting}
          />
        );
      default:
        return null;
    }
  };

  if (hook.loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" tip="Loading SKU data…" />
      </div>
    );
  }

  return (
    <>
      <Card style={{ marginBottom: 24 }}>
        {renderContent()}
      </Card>

      <Space>
        <Button
          icon={<LeftOutlined />}
          onClick={handlePrev}
          disabled={hook.submitting}
          data-testid="migrate-back-btn"
        >
          {isFirstMigrateStep ? 'Change Type' : 'Back'}
        </Button>

        {!isLastMigrateStep && (
          <Button
            type="primary"
            icon={<RightOutlined />}
            iconPosition="end"
            onClick={handleNext}
            disabled={!hook.canGoNext}
            data-testid="migrate-next-btn"
          >
            Next
          </Button>
        )}

        {isLastMigrateStep && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={hook.submitting}
            onClick={() => hook.handleSubmit(onBackToType)}
            data-testid="migrate-submit-btn"
          >
            Convert &amp; Create
          </Button>
        )}
      </Space>
    </>
  );
};

export default SkuMigration;
